import { useEffect, useState } from "react";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { useServices } from "./services/ServiceProvider";
import { ITrustedTypePolicy } from "./services/TrustedTypePolicyManager";
import { AppDispatch, RootState } from "./store/getStore";
import { mergeStyles, useTheme } from "@fluentui/react";

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = (): AppDispatch => useDispatch<AppDispatch>();

export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

/**
 * Hook for getting reference to the HTML sanitizer for localized strings.
 *
 * @returns - The HTML sanitizer
 */
export const useStringsSanitizer = (): ITrustedTypePolicy => useServices().trustedTypePolicyManager.stringsPolicy;

/**
 * A hook that allows monitoring and re-rendering on a media query change.
 *
 * @param query - the media query.
 * @returns - true if the media query matches.
 */
export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const matchQueryList = window.matchMedia(query);
    setMatches(matchQueryList.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setMatches(e.matches);
    };

    matchQueryList.addEventListener("change", handleChange);
    return () => {
      matchQueryList.removeEventListener("change", handleChange);
    };
  }, [query]);
  return matches;
};

export const useSelectStyles = (options?: {
  /**
   * The width
   */
  width?: string | number | undefined;
}): string => {
  const theme = useTheme();

  return mergeStyles({
    color: theme.semanticColors.inputText,
    ...theme.fonts.medium,
    backgroundColor: theme.semanticColors.inputBackground,
    height: 32,
    lineHeight: 30,
    borderColor: theme.semanticColors.inputBorder,
    borderRadius: 2,
    paddingInlineStart: 4,
    paddingInlineEnd: 4,
    width: options?.width,
    "&:focus": {
      outlineColor: theme.semanticColors.inputFocusBorderAlt,
      borderColor: theme.semanticColors.inputFocusBorderAlt,
    },
    "& option": {
      color: theme.semanticColors.inputText,
      backgroundColor: theme.semanticColors.inputBackground,
      ...theme.fonts.medium,
    },
  });
};
